import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Button, Stack, Tooltip, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import IconButton from "@mui/material/IconButton";
import { DescriptionRounded, FileUploadRounded, WarningRounded } from "@mui/icons-material";
import { FilterDef } from "../../../constants/FinanceModels";
import EmptyState from "../../../../../util/component/EmptyState";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { useFuelCardsRepository } from "../../../api/repositories/FuelCardsRepository";
import { STATE_FUEL_ACTIVE_FILTERS, STATE_FUEL_TABLE_UPDATING } from "../constants/FuelCardRecoil";
import FuelModal from "../modals/FuelModal";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import FinanceDialog from "../../../component/FinanceDialog";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import ChipDynamic from "../components/ChipDynamic";
import { IMPORTED } from "../constants/FuelsConstants";
import { SCOPE_FUEL_CARD_REPORT_DELETE, SCOPE_FUEL_CARD_UPLOAD, SCOPE_FUEL_CARD_VIEW_DETAIL } from "../constants/FuelCardAuth";
import { useHasScope } from "../../../../../config/globalConfig";

const FuelCardsReportsTable = (props: any) => {

    const UNIQUE_ID = "fnn-fuel-cards-reports";

    const {t} = useTranslation("finance", {keyPrefix: "fuel"});
    const {getFuelCardsReports, deleteReport} = useFuelCardsRepository();
    const [updating, setUpdating] = useRecoilState(STATE_FUEL_TABLE_UPDATING);
    const filterDefs = useRecoilValue(STATE_FUEL_ACTIVE_FILTERS);
    const [openFuelModal, setOpenFuelModal] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [rowId, setRowId] = useState<string>("");
    const [rowFileName, setRowFileName] = useState<string>("");
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const gridRef = useRef<AgGridReact>(null);
    const theme = useTheme();
    const navigate = useNavigate();
    const hasScope = useHasScope;

    const redrawRows = () => {
        gridRef?.current?.api.redrawRows();
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t("messages.imported"),
                    type: snackbarTypes.success,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const columnDefs : ColDef[] = [
        {
            field: "fileName", headerName: t("tables.fileName"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "createdBy", headerName: t("tables.importedBy"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "createdDate", headerName: t('tables.importingDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L LT") : "",
            unSortIcon: true,
        },
        {
            field: "totalRefuelling", headerName: t("tables.totalRefuelling"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "supplier", headerName: t("tables.supplier"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "legalEntity", headerName: t("tables.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "dealer", headerName: t("tables.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "status", headerName: t("tables.status"),
            minWidth: 150,
            sortable: true, unSortIcon: true,
            lockPosition: "right", lockVisible: true, lockPinned: true, pinned: "right",
            cellRendererSelector: (params) => {
                return {
                    component: () => <ChipDynamic interval={10000}
                                                  id={params.data.id}
                                                  data={params.data}
                                                  initialValue={params.value}
                                                  redrawRows={redrawRows}/>
                };
            },
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (
                        <FinanceDynamicActions>
                            {hasScope(SCOPE_FUEL_CARD_VIEW_DETAIL) &&
                                <Tooltip arrow title={t('tables.tooltips.showDetail')}>
                                    <IconButton disabled={params.data.status.toUpperCase() !== IMPORTED}
                                                sx={{color: theme.palette.accent.main}}
                                                onClick={() => {
                                                    window.open(`/finance/fuelcards/details/${params.data.id}`, "_blank");
                                                }}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>}
                            {hasScope(SCOPE_FUEL_CARD_REPORT_DELETE) &&
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    onClick={() => {
                                        setRowId(params.data.id);
                                        setRowFileName(params.data.fileName);
                                        setOpenDeleteDialog(true)
                                    }
                                    }
                                >
                                    <DeleteIcon/>
                                </IconButton>}
                        </FinanceDynamicActions>
                    ),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.noRowsOverlay.title'),
            content: t("tables.noRowsOverlay.content")
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                getFuelCardsReports(startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;
                        if (result.total === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.total,
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const toolbar = {
        renderActions: () => (
            <Stack>
                {hasScope(SCOPE_FUEL_CARD_UPLOAD) &&
                    <Button fullWidth color={"accent"} variant="contained" component="label"
                         startIcon={<FileUploadRounded/>}
                         onClick={() => setOpenFuelModal(true)}
                >
                    {t('buttons.uploadRefuelFile')}
                </Button>}
            </Stack>
        )
    }

    const deleteRefuelRequest = () => {
        deleteReport(rowId).then(res => {
                if (res.status === "OK") {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("messages.deleteSuccess"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    setUpdating(true);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`snackBarErrors.${res.message}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            }
        );
    }


    return (
        <>
            <AgGridCustomTable
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                toolbar={toolbar}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
            ></AgGridCustomTable>
            <FuelModal open={openFuelModal}
                       onClose={() => {
                           setOpenFuelModal(false)
                       }}
            />
            <FinanceDialog
                title={t(`modalDelete.title`)}
                content={t(`modalDelete.contentForReport`, {file: rowFileName})}
                cancelButton={t('modalDelete.buttons.cancel')}
                open={openDeleteDialog}
                onClose={() => {
                    setOpenDeleteDialog(false);
                }}
                confirmButton={t('modalDelete.buttons.deleteRefuel')}
                callback={() => {
                    deleteRefuelRequest();
                    setOpenDeleteDialog(false);
                }}
                titleIcon={<WarningRounded/>}
            />
        </>

    );
}

export default FuelCardsReportsTable;